import { useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { BrainCircuit, Loader2, ArrowRight, Mail, Lock } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";

// Temporarily hide "Continue with Google" UI while the Emergent-managed OAuth
// consent-screen app name ("Www") is corrected to "CoreText". UI-only switch —
// the underlying Google OAuth integration (google()) is untouched.
// Set to false to restore the button.
const GOOGLE_LOGIN_HIDDEN = true;


function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
      .filter(Boolean)
      .join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export const AccountGate = ({ open, onClose, onAuthed, prompt, platform }) => {
  const { login, register } = useAuth();
  const [mode, setMode] = useState("register");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [agree, setAgree] = useState(false);
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (mode === "register" && !agree) {
      toast.error("Please accept the Terms of Service and Privacy Policy.");
      return;
    }
    setLoading(true);
    try {
      if (mode === "register") {
        await register(email, password, "", agree);
        toast.success("Account created. Your history is saved from here on.");
      } else {
        await login(email, password);
        toast.success("Welcome back.");
      }
      onAuthed();
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const google = () => {
    // Preserve the pending prompt across the Google redirect so we can resume the audit.
    if (prompt) {
      localStorage.setItem("ct_pending_prompt", prompt);
      localStorage.setItem("ct_pending_platform", platform || "");
    }
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <AnimatePresence>
      {open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="account-gate">
          <motion.div
            className="absolute inset-0 bg-[#05060a]/80 backdrop-blur-sm"
            onClick={onClose}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            aria-hidden="true"
          />
          <motion.div
            initial={{ opacity: 0, scale: 0.94, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.94, y: 20 }}
            transition={{ type: "spring", stiffness: 260, damping: 24 }}
            className="relative glass neon-border rounded-2xl w-full max-w-md p-8 scanlines"
          >
            <span className="inline-flex items-center justify-center w-11 h-11 rounded-xl border border-neon-cyan/50 glow-cyan mb-5">
              <BrainCircuit size={22} className="text-neon-cyan" />
            </span>
            <p className="font-mono text-[10px] tracking-[0.3em] text-neon-amber mb-2">
              3 FREE AUDITS USED
            </p>
            <h2 className="font-display text-3xl font-bold tracking-tight leading-none mb-3 text-white">
              Create your free Coretext account.
            </h2>
            <p className="font-mono text-sm text-[var(--muted)] mb-5 leading-relaxed">
              Keep auditing — free. Your account remembers every prompt and build
              pattern so Coretext gets sharper with you and helps your future builds
              land in fewer credits.
            </p>

            {/* Google login temporarily hidden pending correction of the Emergent-managed OAuth consent-screen app name ("Www" → "CoreText"). Underlying OAuth integration (google()) is preserved. To restore, set GOOGLE_LOGIN_HIDDEN = false. */}
            {!GOOGLE_LOGIN_HIDDEN && (
            <>
            <button
              type="button"
              onClick={google}
              data-testid="account-gate-google"
              className="w-full flex items-center justify-center gap-3 rounded-lg border border-neon-cyan/30 bg-[#0b0d14] py-3 font-display font-semibold text-white hover:border-neon-cyan hover:glow-cyan transition-colors transition-shadow duration-200"
            >
              <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden="true">
                <path fill="#FFC107" d="M43.611 20.083H42V20H24v8h11.303c-1.649 4.657-6.08 8-11.303 8-6.627 0-12-5.373-12-12s5.373-12 12-12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4 12.955 4 4 12.955 4 24s8.955 20 20 20 20-8.955 20-20c0-1.341-.138-2.65-.389-3.917z" />
                <path fill="#FF3D00" d="M6.306 14.691l6.571 4.819C14.655 15.108 18.961 12 24 12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4 16.318 4 9.656 8.337 6.306 14.691z" />
                <path fill="#4CAF50" d="M24 44c5.166 0 9.86-1.977 13.409-5.192l-6.19-5.238C29.211 35.091 26.715 36 24 36c-5.202 0-9.619-3.317-11.283-7.946l-6.522 5.025C9.505 39.556 16.227 44 24 44z" />
                <path fill="#1976D2" d="M43.611 20.083H42V20H24v8h11.303a12.04 12.04 0 0 1-4.087 5.571l.003-.002 6.19 5.238C36.971 39.205 44 34 44 24c0-1.341-.138-2.65-.389-3.917z" />
              </svg>
              Continue with Google
            </button>

            <p className="font-mono text-[10px] text-[var(--muted)] mt-2.5 text-center leading-relaxed">
              By continuing with Google you agree to our{" "}
              <Link to="/terms" target="_blank" className="text-neon-cyan hover:underline">Terms</Link> &amp;{" "}
              <Link to="/privacy" target="_blank" className="text-neon-cyan hover:underline">Privacy</Link>.
            </p>

            <div className="flex items-center gap-3 my-4">
              <span className="h-px flex-1 bg-[var(--line)]" />
              <span className="font-mono text-[10px] tracking-[0.25em] text-[var(--muted)]">OR</span>
              <span className="h-px flex-1 bg-[var(--line)]" />
            </div>
            </>
            )}

            <form onSubmit={submit} className="space-y-3">
              <div className="relative">
                <Mail size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--muted)]" />
                <input
                  data-testid="account-gate-email"
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@startup.com"
                  className="w-full bg-[#070910] border border-neon-cyan/25 rounded-lg pl-10 pr-4 py-3 font-mono text-sm text-white placeholder:text-[var(--muted)] focus:outline-none focus:border-neon-cyan focus:glow-cyan transition-colors"
                />
              </div>
              <div className="relative">
                <Lock size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--muted)]" />
                <input
                  data-testid="account-gate-password"
                  type="password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder={mode === "register" ? "Create a password (min 8 chars)" : "Password"}
                  className="w-full bg-[#070910] border border-neon-cyan/25 rounded-lg pl-10 pr-4 py-3 font-mono text-sm text-white placeholder:text-[var(--muted)] focus:outline-none focus:border-neon-cyan focus:glow-cyan transition-colors"
                />
              </div>
              {mode === "register" && (
                <label className="flex items-start gap-2.5 cursor-pointer" data-testid="account-gate-terms">
                  <input
                    type="checkbox"
                    data-testid="account-gate-terms-checkbox"
                    checked={agree}
                    onChange={(e) => setAgree(e.target.checked)}
                    className="mt-0.5 accent-[#22d3ee] w-4 h-4 shrink-0"
                  />
                  <span className="font-mono text-[11px] text-[var(--muted)] leading-relaxed text-left">
                    I agree to the{" "}
                    <Link to="/terms" target="_blank" className="text-neon-cyan hover:underline">Terms</Link>{" "}
                    &amp;{" "}
                    <Link to="/privacy" target="_blank" className="text-neon-cyan hover:underline">Privacy Policy</Link>.
                  </span>
                </label>
              )}
              <button
                data-testid="account-gate-submit"
                type="submit"
                disabled={loading}
                style={{ backgroundColor: "#22d3ee", color: "#05060a" }}
                className="w-full rounded-lg font-display font-bold tracking-wide py-3 flex items-center justify-center gap-2 hover:glow-cyan hover:-translate-y-0.5 transition-transform transition-shadow duration-200 disabled:opacity-50"
              >
                {loading ? (
                  <><Loader2 size={18} className="animate-spin" /> PLEASE WAIT...</>
                ) : (
                  <>{mode === "register" ? "CREATE ACCOUNT & AUDIT" : "SIGN IN & AUDIT"} <ArrowRight size={18} strokeWidth={2.5} /></>
                )}
              </button>
            </form>

            <p className="font-mono text-xs text-[var(--muted)] mt-4 text-center">
              {mode === "register" ? "Already have an account?" : "New to Coretext?"}{" "}
              <button
                type="button"
                data-testid="account-gate-toggle"
                onClick={() => setMode(mode === "register" ? "login" : "register")}
                className="text-neon-cyan hover:underline"
              >
                {mode === "register" ? "Sign in" : "Create a free account"}
              </button>
            </p>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
};
